import React, { useEffect, useState } from 'react';
import { GetServerSideProps } from 'next';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { styled } from '@linaria/react';
import qs from 'qs';
import { dataLayerPush } from '@chotot/web-gtm';
import IDLayout from '~/components/common/IDLayout';
import BoxWithLogo from '~/components/common/BoxWithLogo';
import RegisterForm, { RegisterInputs } from '~/components/register/RegisterForm';
import SocialGroup from '~/components/common/SocialGroup';
import BanPopup from '~/components/common/BanPopup';
import Recaptcha from '~/components/common/Recaptcha';
import { useRegister } from '~/apis/auth';
import { useGetBanStatus } from '~/apis/uba';
import config, { referrerDomain } from '~/config';
import { gtmEventClick, asyncGTMPageMetadata } from '~/helpers/tagging/gtm';
import { setCookiesBySocial } from '~/helpers/cookies';
import { ERR_CODE } from '~/helpers/constants';
import { checkIsMobile } from '~/helpers/util';
const windowRedirect = (url: string) => {
  window.location.href = url;
};
const Register = ({
  isMobile
}: {
  isMobile: boolean;
}) => {
  const {
    query,
    push
  } = useRouter();
  const {
    fetch: register
  } = useRegister();
  const {
    fetch: getBanStatus
  } = useGetBanStatus();
  const [banPopup, setBanPopup] = useState({
    open: false,
    data: null
  });
  const [announcer, setAnnouncer] = useState({
    open: false,
    message: ''
  });
  const [isSubmit, setIsSubmit] = useState(false);
  const isReferrerDomain = referrerDomain.some((elm: string) => query.continue && query.continue.indexOf(elm) > -1);
  const defaultPhone = query.service === 'flashad' && query.phone ? String(query.phone) : '';
  useEffect(() => {
    asyncGTMPageMetadata({
      page: 'sign_up',
      rest: {
        identifier_category: 'non-logged_in'
      }
    });
  }, []);

  // e.g: /register -> /login
  // to keep "continue" query for flash-ad and job domain
  const queryForCrossRoute = () => {
    const finalQuery = {
      ...query,
      continue: query.service === 'flashad' ? config.lightAdUrl : query.continue
    };
    if (isReferrerDomain || query.continue?.includes('aff_sid')) {
      finalQuery.continue = query.continue;
    }
    return qs.stringify(finalQuery);
  };
  const isKeepQuery = query.service === 'flashad' || isReferrerDomain;
  const onSubmit = async (data: RegisterInputs) => {
    const {
      phone,
      password,
      name,
      acceptPolicy
    } = data;
    setAnnouncer({
      open: false,
      message: ''
    });
    if (!acceptPolicy) {
      setAnnouncer({
        open: true,
        message: 'Bạn cần đồng ý với Điều khoản sử dụng và Chính sách bảo mật của Chợ Tốt.'
      });
      return;
    }
    setIsSubmit(true);
    const token = await Recaptcha.execute('REGISTER');
    if (token) {
      gtmEventClick({
        category: 'sign_up',
        label: 'register_button'
      });
      const tiktokPixelData = {
        tiktok_pixel_event: 'sso/REGISTRATION_SUCCESS',
        payload: {}
      };
      dataLayerPush({
        event: 'ct_tiktok_pixel',
        event_label: JSON.stringify(tiktokPixelData)
      });
      const {
        error
      } = await register({
        phone,
        password,
        fullName: name,
        token
      });
      if (error) {
        setIsSubmit(false);
        handleError(error);
        return;
      }
      if (isKeepQuery) {
        push(`/register/otp?phone=${phone}&${queryForCrossRoute()}`);
      } else {
        push({
          pathname: '/register/otp',
          query: {
            ...query,
            phone
          }
        });
      }
    }
  };
  const handleError = async (error: any) => {
    if (error.err_code === ERR_CODE.TERMINATED) {
      if (error.ext?.account_id) {
        const {
          data
        } = await getBanStatus(error.ext.account_id);
        if (data?.success) {
          const banInfo = data.data.accounts[0];
          setBanPopup({
            open: true,
            data: banInfo
          });
        }
      }
    } else {
      setAnnouncer({
        open: true,
        message: error.message || error.error_message || 'Đã có lỗi xảy ra, vui lòng thử lại sau.'
      });
    }
  };
  const onSocialLoggedIn = (data: any, error: any) => {
    if (data && data.success) {
      setCookiesBySocial(data.data);
      windowRedirect(data.data.redirect_to);
    } else {
      handleError(error);
    }
  };
  const goLogin = () => {
    gtmEventClick({
      category: 'sign_up',
      label: 'log_in'
    });
  };
  const onInvalid = () => {
    setAnnouncer({
      open: false,
      message: ''
    });
  };
  return <IDLayout isMobile={isMobile}>
      <Recaptcha />
      <BoxWithLogo>
        <BoxWithLogo.Title>Đăng ký tài khoản</BoxWithLogo.Title>
        <BoxWithLogo.ErrorArea open={announcer.open} message={announcer.message} mb="16px" />

        <RegisterForm defaultValue={{
        phone: defaultPhone
      }} disable={isSubmit} onSubmit={onSubmit} onInvalid={onInvalid} />

        <SocialGroup title="Hoặc đăng nhập bằng" onLoggedIn={onSocialLoggedIn} />
        <LoginText>
          Đã có tài khoản?{' '}
          <Link onClick={goLogin} href={`/?continue=${query.continue || ''}`}>
            Đăng nhập ngay
          </Link>
        </LoginText>
      </BoxWithLogo>

      <BanPopup open={banPopup.open} data={banPopup.data} onClose={() => setBanPopup({
      ...banPopup,
      open: false
    })} />
    </IDLayout>;
};
const LoginText = /*#__PURE__*/styled('p')({
  name: "LoginText",
  class: "l1pp625j",
  propsAsIs: false
});
export const getServerSideProps: GetServerSideProps = async ({
  req
}) => {
  const isMobile = checkIsMobile(req.headers['user-agent'] || '');
  return {
    props: {
      isMobile
    }
  };
};
export default Register;

require("./index.linaria.module.css!=!../../../node_modules/.pnpm/@linaria+webpack5-loader@4.1.17_webpack@5.95.0/node_modules/@linaria/webpack5-loader/lib/outputCssLoader.js?cacheProvider=!./index.tsx");